
import { useState, useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import PersonalityTestHistoryHeader from '@/components/personality/PersonalityTestHistoryHeader';
import PersonalityTestHistoryTabs from '@/components/personality/PersonalityTestHistoryTabs';
import PersonalityTestCard from '@/components/personality/PersonalityTestCard';
import EmptyTestHistoryState from '@/components/personality/EmptyTestHistoryState';
import EnhancedAdvancedAnalysisModal from '@/components/personality/EnhancedAdvancedAnalysisModal';
import { usePersonalityRepository, useYoungSchemasRepository } from '@/hooks/useRepositoryFactory';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Star, Brain } from 'lucide-react';
import { motion } from 'framer-motion';
import { CircleDecor } from '@/components/ui/visual-elements';

export default function PersonalityTestHistory() {
  const [activeTab, setActiveTab] = useState('big-five');
  const [bigFiveResults, setBigFiveResults] = useState([]);
  const [youngSchemasResults, setYoungSchemasResults] = useState([]);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const personalityRepository = usePersonalityRepository();
  const youngSchemasRepository = useYoungSchemasRepository();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadTestHistory();
    }
  }, [user]);

  const loadTestHistory = async () => {
    try {
      setIsLoading(true);
      
      if (personalityRepository && youngSchemasRepository) {
        const [bigFiveData, youngSchemasData] = await Promise.all([
          personalityRepository.getResultsHistory(user.id),
          youngSchemasRepository.getResultsHistory(user.id)
        ]);

        // Transform Big Five results with enhanced analysis detection
        const transformedBigFive = bigFiveData.map(result => ({
          id: result.id,
          type: 'big-five',
          typeName: 'Big Five (OCEAN)',
          icon: Star,
          date: new Date(result.createdAt).toLocaleDateString('pt-BR'),
          scores: {
            openness: result.openness,
            conscientiousness: result.conscientiousness,
            extraversion: result.extraversion,
            agreeableness: result.agreeableness,
            neuroticism: result.neuroticism
          },
          summary: `Análise dos cinco grandes fatores de personalidade realizada em ${new Date(result.createdAt).toLocaleDateString('pt-BR')}`,
          createdAt: result.createdAt,
          updatedAt: result.updatedAt,
          analysisContent: null // Will be loaded when viewing analysis
        }));

        // Transform Young Schemas results with enhanced analysis detection
        const transformedYoungSchemas = youngSchemasData.map(result => ({
          id: result.id,
          type: 'young-schemas',
          typeName: 'Esquemas de Young',
          icon: Brain,
          date: new Date(result.createdAt).toLocaleDateString('pt-BR'),
          scores: {
            abandonment: result.abandonment,
            mistrustAbuse: result.mistrustAbuse,
            emotionalDeprivation: result.emotionalDeprivation,
            defectiveness: result.defectiveness,
            socialIsolation: result.socialIsolation
          },
          summary: `Avaliação dos esquemas cognitivos mal-adaptativos realizada em ${new Date(result.createdAt).toLocaleDateString('pt-BR')}`,
          createdAt: result.createdAt,
          updatedAt: result.updatedAt,
          analysisContent: null // Will be loaded when viewing analysis
        }));

        setBigFiveResults(transformedBigFive);
        setYoungSchemasResults(transformedYoungSchemas);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de testes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewResult = (result) => {
    if (result.type === 'big-five') {
      navigate(`/testes-personalidade/big-five/resultado/${result.id}`);
    } else if (result.type === 'young-schemas') {
      navigate(`/testes-personalidade/young-schemas/resultado/${result.id}`);
    }
  };

  const handleViewAnalysis = async (analysisContent) => {
    try {
      // Enhanced analysis handling for ultra-detailed reports
      if (analysisContent && typeof analysisContent === 'object') {
        setSelectedAnalysis(analysisContent);
        setSelectedInsight({
          id: 'analysis-' + Date.now(),
          created_at: new Date().toISOString()
        });
        setShowAnalysisModal(true);
      } else {
        console.log('Análise não disponível ou formato inválido');
      }
    } catch (error) {
      console.error('Erro ao carregar análise:', error);
    }
  };

  const activeResults = activeTab === 'big-five' ? bigFiveResults : youngSchemasResults;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p>Carregando histórico completo de testes...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto p-6 relative">
        <CircleDecor className="absolute -top-10 -right-10 z-0 opacity-10" size="lg" />
        <CircleDecor className="absolute top-40 -left-10 z-0 opacity-10" variant="secondary" />
        
        <PersonalityTestHistoryHeader />
        
        <PersonalityTestHistoryTabs 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />

        <div className="mt-8">
          {activeResults.length === 0 ? (
            <EmptyTestHistoryState activeTab={activeTab} />
          ) : (
            <div className="space-y-6">
              {activeResults.map((result, index) => (
                <PersonalityTestCard
                  key={result.id}
                  result={result}
                  isFirst={index === 0}
                  onViewResult={() => handleViewResult(result)}
                  onViewAnalysis={handleViewAnalysis}
                />
              ))}
            </div>
          )}
        </div>

        {showAnalysisModal && selectedAnalysis && selectedInsight && (
          <EnhancedAdvancedAnalysisModal
            isOpen={showAnalysisModal}
            onClose={() => setShowAnalysisModal(false)}
            analysis={selectedAnalysis}
            insight={selectedInsight}
          />
        )}
      </div>
    </MainLayout>
  );
}
